/**
 * MessageListFilters - Filter bar component for message list
 *
 * Reuses UI from FilterComponent.jsx, logic from useMessageFilters hook
 */

import React, { memo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Calendar } from 'primereact/calendar';
import { Tooltip } from 'primereact/tooltip';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';

import useFilterBreakpoint from 'hooks/useFilterBreakpoint';
import { useDebounce } from 'hooks/useDebounce';
import { useMessageContext } from '../../context';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { msgStatusOptions, participantOptions, sentimentOptions } from 'constants/dropdownOptions';
import CaseListDropDown from 'components/CaseListDropDown/CaseListDropDown';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { LoaderComponent } from 'shared/LoaderComponent';
import { assignToItemTemplate } from 'modules/Tasks/components/TaskBoard/Board/PriorityDropdownTemplate';
import { LABELS, FILTER_PLACEHOLDERS, TOOLTIPS } from '../../constants/constants';

function MessageListFilters({ filterState, authorsList, clientsList, isAuthorLoading }) {
  const { isGlobal, isFromClientPortal } = useMessageContext();
  const userContext = useUserDetailsContext();
  const { showFilters, setShowFilters } = useFilterBreakpoint();

  // Local state for immediate input updates
  const [searchInput, setSearchInput] = useState(filterState.filters.search);

  // Debounced search value
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Sync local state with filter state when it changes externally (e.g., reset)
  useEffect(() => {
    setSearchInput(filterState.filters.search);
  }, [filterState.filters.search]);

  // Trigger search when debounced value changes
  useEffect(() => {
    if (debouncedSearchInput !== filterState.filters.search) {
      filterState.updateFilter('search', debouncedSearchInput, null);
    }
  }, [debouncedSearchInput, filterState]);

  const handleSearchSubmit = useCallback(() => {
    // Trigger immediate search on submit (bypass debounce)
    filterState.updateFilter('search', searchInput, null);
  }, [searchInput, filterState]);

  const handleSearchInputChange = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  return (
    <div className="d-flex flex-wrap align-items-center filter-wrap mb-3 p-2 w-100 align-items-center justify-content-between gap-2">
      {/* Search */}
      {(isGlobal || isFromClientPortal) && (
        <div className="filter-wraper">
          <span className="p-input-icon-right input-shadow w-100">
            <i className="icon-search F-size16 cursor-pointer" onClick={handleSearchSubmit} />
            <TextSnippetWrapper
              type="text"
              placeholder={FILTER_PLACEHOLDERS.SEARCH}
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              className="input-search"
              autoComplete="off"
            />
          </span>
        </div>
      )}

      {/* Mobile filter toggle */}
      <div className="d-block d-lg-none w-100">
        <Button
          className="p-button p-component btn p-button-secondary outline w-100 filter-toggle-btn"
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters}
          aria-controls="msg-filter-section"
          label="Filters"
        >
          <span
            style={{
              fontSize: 18,
              transition: 'transform 0.2s',
              display: 'inline-block',
              transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <i className="fa fa-solid fa-angle-down"></i>
          </span>
        </Button>
      </div>

      {/* Filter controls */}
      <div
        className={`d-flex flex-wrap align-items-center gap-2 filter-toggle-section filter-section${showFilters ? ' show' : ''}`}
        id="msg-filter-section"
        aria-hidden={!showFilters}
      >
        <label className="text-bold black-600 me-2 filter-label">{LABELS.FILTERS_LABEL}</label>

        {/* Case/Lead filter */}
        {isGlobal && !isFromClientPortal && (
          <div className="d-flex flex-column filter-wraper">
            <CaseListDropDown
              isClient={isFromClientPortal}
              value={filterState.filters.case}
              onChange={(e) => filterState.filters.case !== e && filterState.updateFilter('case', e, 'case')}
              placeholder={FILTER_PLACEHOLDERS.BY_CASE_LEAD}
              noOptionsMessage={FILTER_PLACEHOLDERS.NO_CASES_FOUND}
              name="case"
              isClearable
              excludeFromLoading
            />
          </div>
        )}
        {(isGlobal || isFromClientPortal) && userContext?.clientCaseList?.length > 1 && (
          <div className="d-flex flex-column filter-wraper">
            <CaseListDropDown
              isClient={isFromClientPortal}
              value={filterState.filters.case}
              onChange={(e) => filterState.filters.case !== e && filterState.updateFilter('case', e, 'case')}
              name="case"
              excludeFromLoading
            />
          </div>
        )}

        {/* Author filter - Single select for V2 API */}
        <div className="d-flex flex-column filter-wraper">
          <Dropdown
            placeholder={FILTER_PLACEHOLDERS.BY_AUTHOR}
            options={authorsList}
            className="input-shadow F-size14"
            filter
            filterPlaceholder={FILTER_PLACEHOLDERS.SEARCH}
            optionLabel="assignee_name"
            itemTemplate={assignToItemTemplate}
            value={clientsList?.length > 0 ? filterState.filters.author : null}
            onChange={(e) => filterState.updateFilter('author', e.value, 'author')}
            optionGroupLabel="assignee_name"
            optionGroupChildren="items"
            optionValue="value"
            resetFilterOnHide
            showClear
            panelFooterTemplate={isAuthorLoading ? LoaderComponent : null}
          />
        </div>

        {/* Date filter */}
        <div className="d-flex flex-column filter-wraper">
          <Calendar
            id="basic"
            value={filterState.filters.date}
            onChange={(e) => filterState.updateFilter('date', e.value || [], 'date')}
            placeholder={FILTER_PLACEHOLDERS.BY_DATE}
            className="cursor input-shadow by-date"
            monthNavigator
            yearNavigator
            selectionMode="range"
            yearRange="2010:2030"
            showButtonBar
          />
        </div>

        {/* Status filter */}
        <div className="d-flex flex-column filter-wraper">
          <Dropdown
            value={filterState.filters.status}
            options={isFromClientPortal ? msgStatusOptions.filter((opt) => opt.value !== 'scheduled') : msgStatusOptions}
            onChange={(e) => filterState.updateFilter('status', e.value === 'All' ? '' : e.value, 'message_status')}
            placeholder={FILTER_PLACEHOLDERS.BY_MESSAGE_STATUS}
          />
        </div>

        {/* Participant filter */}
        {!isFromClientPortal && (
          <div className="d-flex flex-column filter-wraper">
            <Dropdown
              value={filterState.filters.participant}
              options={participantOptions}
              onChange={(e) => filterState.updateFilter('participant', e.value, 'participants')}
              placeholder={FILTER_PLACEHOLDERS.BY_PARTICIPANTS}
            />
          </div>
        )}

        {/* Sentiment filter */}
        {!isFromClientPortal && (
          <div className="d-flex flex-column filter-wraper">
            <Dropdown
              value={filterState.filters.sentiment}
              options={sentimentOptions}
              onChange={(e) => filterState.updateFilter('sentiment', e.value, 'sentiment')}
              placeholder={FILTER_PLACEHOLDERS.BY_SENTIMENT}
            />
          </div>
        )}

        {/* Reset */}
        <div className="pointer me-0 p-2">
          <i className="fas fa-sync-alt" onClick={filterState.resetFilters}></i>
          <Tooltip content={TOOLTIPS.RESET_FILTER} position="top" target=".fa-sync-alt" showDelay={500} />
        </div>
      </div>
    </div>
  );
}

MessageListFilters.propTypes = {
  filterState: PropTypes.shape({
    filters: PropTypes.shape({
      case: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      author: PropTypes.string,
      date: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
      status: PropTypes.string,
      sentiment: PropTypes.string,
      participant: PropTypes.string,
      search: PropTypes.string,
    }).isRequired,
    updateFilter: PropTypes.func.isRequired,
    resetFilters: PropTypes.func.isRequired,
  }).isRequired,
  authorsList: PropTypes.arrayOf(
    PropTypes.shape({
      assignee_name: PropTypes.string,
      assignee_id: PropTypes.string,
      items: PropTypes.array,
    })
  ),
  clientsList: PropTypes.array,
  isAuthorLoading: PropTypes.bool,
};

MessageListFilters.defaultProps = {
  authorsList: [],
  clientsList: [],
  isAuthorLoading: false,
};

export default memo(MessageListFilters);
